import { ArrowLeft, Mail, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import styles from "./LegalPage.module.css";
import Footer from "../../ui/Footer/Footer";
const sections = [
  { id: "intro", title: "Agreement Overview" },
  { id: "acceptance", title: "Acceptance of Terms" },
  { id: "eligibility", title: "Eligibility" },
  { id: "account", title: "Account Registration" },
  { id: "services", title: "Description of Services" },
  { id: "acceptable-use", title: "Acceptable Use Policy" },
  { id: "intellectual-property", title: "Intellectual Property" },
  { id: "user-content", title: "User Content & Data" },
  { id: "payment", title: "Payment & Billing" },
  { id: "termination", title: "Termination" },
  { id: "liability", title: "Limitation of Liability" },
  { id: "indemnification", title: "Indemnification" },
  { id: "governing-law", title: "Governing Law" },
  { id: "changes", title: "Changes to Terms" },
];

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState("intro");

  const handleClick = (id) => {
    setActiveSection(id);
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={styles.page}>
      {/* Dark Hero Banner */}
      <div className={styles.heroBanner}>
        <div className={styles.heroInner}>
          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <h1 className={styles.heroTitle}>Terms of Service</h1>
          <p className={styles.heroMeta}>Effective date: March 1, 2026</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        <div className={styles.tabBarInner}>
          <Link to="/privacy-policy" className={styles.tab}>
            Privacy Statement
          </Link>
          <Link
            to="/terms-of-service"
            className={`${styles.tab} ${styles.tabActive}`}
          >
            Terms of Service
          </Link>
        </div>
      </div>

      {/* Layout: Sidebar + Content */}
      <div className={styles.layout}>
        {/* Sidebar Navigation */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarTitle}>On this page</div>
          <nav className={styles.sidebarNav}>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`${styles.sidebarLink} ${activeSection === s.id ? styles.sidebarLinkActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(s.id);
                }}
              >
                {s.title}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={styles.mainContent}>
          {/* Intro */}
          <div className={styles.section} id="intro">
            <h2 className={styles.sectionTitle}>Agreement Overview</h2>
            <div className={styles.sectionBody}>
              <p>
                Welcome to <strong>viewStruct</strong>. These Terms of Service
                ("Terms") constitute a legally binding agreement between you
                ("User," "you," or "your") and viewStruct ("Company," "we,"
                "us," or "our") governing your access to and use of the
                viewStruct platform, including all associated services,
                features, and tools.
              </p>
              <div className={styles.highlightBox}>
                Please read these Terms carefully before using our platform. By
                accessing or using viewStruct, you agree to be bound by these
                Terms. If you do not agree to these Terms, you must not access
                or use our services.
              </div>
            </div>
          </div>

          {/* 1. Acceptance */}
          <div className={styles.section} id="acceptance">
            <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
            <div className={styles.sectionBody}>
              <p>
                By creating an account, accessing, or using the viewStruct
                platform, you acknowledge that you have read, understood, and
                agree to be bound by these Terms, as well as our Privacy Policy.
              </p>
              <ul>
                <li>
                  <strong>Binding agreement:</strong> These Terms form a binding
                  contract between you and viewStruct upon your first access or
                  use of our platform
                </li>
                <li>
                  <strong>Capacity to agree:</strong> You represent that you
                  have the legal authority to enter into these Terms on behalf
                  of yourself or the organization you represent
                </li>
                <li>
                  <strong>Additional terms:</strong> Certain features or
                  services may be subject to additional terms, which will be
                  presented to you at the time of use
                </li>
                <li>
                  <strong>Conflicts:</strong> In the event of a conflict between
                  these Terms and any additional terms, the additional terms
                  shall prevail for that specific feature or service
                </li>
              </ul>
            </div>
          </div>

          {/* 2. Eligibility */}
          <div className={styles.section} id="eligibility">
            <h2 className={styles.sectionTitle}>2. Eligibility</h2>
            <div className={styles.sectionBody}>
              <p>
                To use viewStruct, you must meet the following eligibility
                requirements:
              </p>
              <ul>
                <li>
                  <strong>Age requirement:</strong> You must be at least 18
                  years of age, or the age of legal majority in your
                  jurisdiction
                </li>
                <li>
                  <strong>Professional use:</strong> viewStruct is designed for
                  professional and business use within the AEC industries
                </li>
                <li>
                  <strong>Organizational authorization:</strong> If using on
                  behalf of an organization, you represent that you are
                  authorized to bind that organization
                </li>
                <li>
                  <strong>Compliance:</strong> You must not be prohibited from
                  using our services under any applicable laws
                </li>
              </ul>
              <div className={styles.warningBox}>
                viewStruct reserves the right to refuse service, terminate
                accounts, or cancel orders at our discretion if eligibility
                requirements are not met.
              </div>
            </div>
          </div>

          {/* 3. Account Registration */}
          <div className={styles.section} id="account">
            <h2 className={styles.sectionTitle}>3. Account Registration</h2>
            <div className={styles.sectionBody}>
              <p>
                To access certain features, you must create an account. When
                registering, you agree to:
              </p>
              <ul>
                <li>
                  <strong>Accurate information:</strong> Provide accurate,
                  current, and complete information and keep it updated
                </li>
                <li>
                  <strong>Account security:</strong> Maintain the security of
                  your login credentials. You are responsible for all activities
                  under your account
                </li>
                <li>
                  <strong>Unauthorized access:</strong> Notify us immediately at{" "}
                  <strong>security@viewstruct.com</strong> if you suspect
                  unauthorized use
                </li>
                <li>
                  <strong>One account per person:</strong> Each individual may
                  only maintain one account unless expressly authorized
                </li>
                <li>
                  <strong>Account sharing:</strong> You may not share your
                  credentials. Team access should use our organization features
                </li>
              </ul>
            </div>
          </div>

          {/* 4. Description of Services */}
          <div className={styles.section} id="services">
            <h2 className={styles.sectionTitle}>4. Description of Services</h2>
            <div className={styles.sectionBody}>
              <p>
                viewStruct provides a cloud-based platform for the AEC industry,
                including:
              </p>
              <ul>
                <li>
                  <strong>3D/2D Model Viewing:</strong> Browser-based
                  visualization of IFC, RVT, DWG, and other CAD/BIM formats
                </li>
                <li>
                  <strong>File Management:</strong> Secure cloud storage with
                  version control and advanced search
                </li>
                <li>
                  <strong>Collaboration Tools:</strong> Real-time collaboration
                  including shared views, comments, and markups
                </li>
                <li>
                  <strong>Project Management:</strong> Tools for organizing
                  projects, tasks, and deadlines
                </li>
                <li>
                  <strong>API Access:</strong> RESTful APIs for third-party
                  integrations
                </li>
                <li>
                  <strong>Analytics & Reporting:</strong> Insights into project
                  activity and team productivity
                </li>
              </ul>
              <div className={styles.highlightBox}>
                We continuously improve our services and may add, modify, or
                discontinue features with reasonable notice.
              </div>
            </div>
          </div>

          {/* 5. Acceptable Use */}
          <div className={styles.section} id="acceptable-use">
            <h2 className={styles.sectionTitle}>5. Acceptable Use Policy</h2>
            <div className={styles.sectionBody}>
              <p>
                You agree to use viewStruct only for lawful purposes. You shall
                not:
              </p>
              <ul>
                <li>
                  <strong>Illegal activities:</strong> Use our platform for any
                  unlawful or prohibited purpose
                </li>
                <li>
                  <strong>Harmful content:</strong> Upload malicious, harmful,
                  or objectionable content
                </li>
                <li>
                  <strong>Security violations:</strong> Attempt unauthorized
                  access to any part of the platform
                </li>
                <li>
                  <strong>Reverse engineering:</strong> Decompile or attempt to
                  derive source code
                </li>
                <li>
                  <strong>Service disruption:</strong> Interfere with platform
                  integrity or performance
                </li>
                <li>
                  <strong>Data scraping:</strong> Use automated tools to extract
                  data without permission
                </li>
                <li>
                  <strong>Misrepresentation:</strong> Impersonate any person or
                  entity
                </li>
              </ul>
              <div className={styles.warningBox}>
                Violation may result in immediate suspension or termination
                without prior notice.
              </div>
            </div>
          </div>

          {/* 6. Intellectual Property */}
          <div className={styles.section} id="intellectual-property">
            <h2 className={styles.sectionTitle}>6. Intellectual Property</h2>
            <div className={styles.sectionBody}>
              <p>
                All intellectual property rights relating to the viewStruct
                platform are owned by or licensed to viewStruct:
              </p>
              <ul>
                <li>
                  <strong>Platform ownership:</strong> The platform, including
                  design, code, and branding, is our exclusive property
                </li>
                <li>
                  <strong>License grant:</strong> We grant you a limited,
                  non-exclusive, non-transferable, revocable license to use the
                  platform
                </li>
                <li>
                  <strong>Restrictions:</strong> You may not copy, modify,
                  distribute, or create derivative works without consent
                </li>
                <li>
                  <strong>Trademarks:</strong> "viewStruct" and related marks
                  may not be used without permission
                </li>
                <li>
                  <strong>Feedback:</strong> Any feedback you provide may be
                  used by viewStruct without obligation
                </li>
              </ul>
            </div>
          </div>

          {/* 7. User Content */}
          <div className={styles.section} id="user-content">
            <h2 className={styles.sectionTitle}>7. User Content & Data</h2>
            <div className={styles.sectionBody}>
              <p>You retain ownership of all content you upload:</p>
              <ul>
                <li>
                  <strong>Your ownership:</strong> You retain all rights in your
                  uploaded files, models, and project data
                </li>
                <li>
                  <strong>License to us:</strong> You grant viewStruct a limited
                  license to store, process, and display your content for
                  service delivery
                </li>
                <li>
                  <strong>Content responsibility:</strong> You are responsible
                  for all content you upload
                </li>
                <li>
                  <strong>Backup recommendation:</strong> We recommend
                  maintaining your own backups of critical files
                </li>
              </ul>
              <div className={styles.highlightBox}>
                We will never sell or use your uploaded files for any purpose
                other than delivering our services. Your CAD/BIM models remain
                strictly confidential.
              </div>
            </div>
          </div>

          {/* 8. Payment */}
          <div className={styles.section} id="payment">
            <h2 className={styles.sectionTitle}>8. Payment & Billing</h2>
            <div className={styles.sectionBody}>
              <p>If you subscribe to a paid plan:</p>
              <ul>
                <li>
                  <strong>Pricing:</strong> All prices are in USD unless
                  otherwise stated and are exclusive of applicable taxes
                </li>
                <li>
                  <strong>Billing cycle:</strong> Subscriptions are billed
                  monthly or annually
                </li>
                <li>
                  <strong>Auto-renewal:</strong> Subscriptions automatically
                  renew unless cancelled
                </li>
                <li>
                  <strong>Refunds:</strong> 14-day money-back guarantee for new
                  subscriptions
                </li>
                <li>
                  <strong>Plan changes:</strong> Upgrades take effect
                  immediately; downgrades at the next billing cycle
                </li>
                <li>
                  <strong>Late payments:</strong> We may suspend access for
                  failed or overdue payments
                </li>
              </ul>
            </div>
          </div>

          {/* 9. Termination */}
          <div className={styles.section} id="termination">
            <h2 className={styles.sectionTitle}>9. Termination</h2>
            <div className={styles.sectionBody}>
              <p>Either party may terminate this agreement:</p>
              <ul>
                <li>
                  <strong>By you:</strong> You may terminate your account at any
                  time via settings or by contacting us
                </li>
                <li>
                  <strong>By us:</strong> We may suspend or terminate your
                  account for Terms violations
                </li>
                <li>
                  <strong>Data after termination:</strong> Data retained for 30
                  days for export, then permanently deleted within 90 days
                </li>
                <li>
                  <strong>Surviving provisions:</strong> IP, liability,
                  indemnification, and governing law sections survive
                  termination
                </li>
              </ul>
              <div className={styles.warningBox}>
                Before deleting your account, export all your files and data.
                Once permanently deleted, it cannot be recovered.
              </div>
            </div>
          </div>

          {/* 10. Liability */}
          <div className={styles.section} id="liability">
            <h2 className={styles.sectionTitle}>10. Limitation of Liability</h2>
            <div className={styles.sectionBody}>
              <p>To the maximum extent permitted by applicable law:</p>
              <ul>
                <li>
                  <strong>No warranty:</strong> The platform is provided "as is"
                  without warranties of any kind
                </li>
                <li>
                  <strong>Liability cap:</strong> Total liability shall not
                  exceed amounts paid in the twelve months preceding the claim
                </li>
                <li>
                  <strong>Excluded damages:</strong> We are not liable for
                  indirect, incidental, special, or consequential damages
                </li>
                <li>
                  <strong>Third-party services:</strong> We are not responsible
                  for third-party providers integrated with the platform
                </li>
              </ul>
            </div>
          </div>

          {/* 11. Indemnification */}
          <div className={styles.section} id="indemnification">
            <h2 className={styles.sectionTitle}>11. Indemnification</h2>
            <div className={styles.sectionBody}>
              <p>
                You agree to indemnify and hold harmless viewStruct from claims
                arising from:
              </p>
              <ul>
                <li>Your use or misuse of the platform</li>
                <li>Your violation of these Terms or applicable law</li>
                <li>Your infringement of any intellectual property rights</li>
                <li>Any content you upload or transmit through the platform</li>
                <li>
                  Any negligent or wrongful conduct in connection with the
                  platform
                </li>
              </ul>
            </div>
          </div>

          {/* 12. Governing Law */}
          <div className={styles.section} id="governing-law">
            <h2 className={styles.sectionTitle}>
              12. Governing Law & Dispute Resolution
            </h2>
            <div className={styles.sectionBody}>
              <ul>
                <li>
                  <strong>Jurisdiction:</strong> Disputes subject to courts
                  where viewStruct is incorporated
                </li>
                <li>
                  <strong>Dispute resolution:</strong> Good-faith negotiation
                  for at least 30 days before formal proceedings
                </li>
                <li>
                  <strong>Arbitration:</strong> Binding arbitration if
                  negotiation fails
                </li>
                <li>
                  <strong>Class action waiver:</strong> You agree to resolve
                  disputes individually
                </li>
                <li>
                  <strong>Injunctive relief:</strong> Either party may seek
                  equitable relief in any competent court
                </li>
              </ul>
            </div>
          </div>

          {/* 13. Changes */}
          <div className={styles.section} id="changes">
            <h2 className={styles.sectionTitle}>13. Changes to These Terms</h2>
            <div className={styles.sectionBody}>
              <p>
                We reserve the right to modify these Terms at any time. Material
                changes will be communicated with at least 30 days' notice
                through:
              </p>
              <ul>
                <li>A prominent notice on our platform</li>
                <li>An email notification to your registered address</li>
                <li>
                  An in-app notification when you next access the platform
                </li>
              </ul>
              <p>
                Continued use after the effective date constitutes acceptance of
                the revised Terms.
              </p>
            </div>
          </div>
        </main>
      </div>

      {/* Contact Footer */}
      {/* <div className={styles.contactFooter}>
        <div className={styles.contactFooterInner}>
          <div className={styles.contactFooterText}>
            <h3>Questions About Our Terms?</h3>
            <p>Our legal team is ready to assist you.</p>
          </div>
          <div className={styles.contactFooterLinks}>
            <button className={styles.contactBtn}>
              <Mail size={15} />
              legal@viewstruct.com
            </button>
            <button
              className={`${styles.contactBtn} ${styles.contactBtnOutline}`}
            >
              <Globe size={15} />
              Support Center
            </button>
          </div>
        </div>
      </div> */}
      <Footer />
    </div>
  );
};

export default TermsOfService;
